import React, { useContext, useState, useEffect } from 'react';
import type { UseFormHandleSubmit } from 'react-hook-form';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import { Typography } from 'ui/typography';
import Button from 'components/partials/button/button';
import NotificationMessage from 'components/partials/notification/notification';

import PersonalInformationForm from 'containers/admin/users/user/forms/personal-info-form/personal-info-form';
import UserDetailsForm from 'containers/admin/users/user/forms/user-details-form/user-details-form';
import { UserProfileContext } from 'containers/admin/users/user/user-profile/user-profile';

interface ClientLike {
  id?: string | number;
  name?: string;
  authorizedDomains?: string;
  [key: string]: unknown;
}

interface UserFormProps {
  isMVUser?: boolean;
  userRole?: string;
  currentClient?: ClientLike | null;
  isFormDisabled?: boolean;
  isSelectDisabled?: boolean;
  isNotNewUser?: boolean;
  isCurrentUserProfile?: boolean;
  onCancel: () => void;
  onSubmit: (formData: Record<string, unknown>) => void;
  handleSubmit: UseFormHandleSubmit<Record<string, unknown>>;
}

const UserForm = ({
  isMVUser,
  userRole,
  currentClient,
  isFormDisabled,
  isSelectDisabled,
  isNotNewUser,
  isCurrentUserProfile,
  onCancel,
  onSubmit,
  handleSubmit,
}: UserFormProps): React.JSX.Element => {
  const { errors } = useContext(UserProfileContext);
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState<string | undefined>(
    userRole
  );

  const isStacked = isFormDisabled || isNotNewUser;

  useEffect(() => {
    setIsSaveBtnDisabled(Object.keys(errors).length !== 0);
  }, [Object.keys(errors).length]);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" sx={{ width: '100%' }}>
        {!isCurrentUserProfile && (
          <Typography
            data-testid="user-form-personal-info-heading"
            variant="h3"
            sx={{ width: '50%', mr: 8, mt: 2 }}
          >
            Personal information
          </Typography>
        )}
        {!isFormDisabled && !isNotNewUser && (
          <Typography
            data-testid="user-form-user-details-heading"
            variant="h3"
            sx={{ width: '50%', mt: 2 }}
          >
            User details
          </Typography>
        )}
      </Stack>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}
      >
        <Stack direction={isStacked ? 'column' : 'row'}>
          <PersonalInformationForm
            {...{
              isNotNewUser,
              isFormDisabled,
              isMVUser,
            }}
          />
          <UserDetailsForm
            {...{
              currentUserRole,
              currentClient: currentClient as never,
              isNotNewUser,
              isFormDisabled,
              isSelectDisabled,
              isMVUser,
              isCurrentUserProfile,
            }}
          />
          {!isFormDisabled && isMVUser && (
            <Stack direction="row">
              <NotificationMessage
                heading="Note"
                customClass="mb-5"
                variant="info"
                message="Locked items cannot be edited. Contact your institution administrator or MARKETview Consultant to make changes."
              />
            </Stack>
          )}
        </Stack>
        {!isFormDisabled && (
          <Stack direction="row" sx={{ justifyContent: 'flex-end', mt: 2 }}>
            <Button
              type="button"
              variant="reserve-text"
              size="md"
              onClick={(e) => {
                e.preventDefault();
                setCurrentUserRole(userRole);
                onCancel();
              }}
              data-testid="cancel-user-form-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaveBtnDisabled}
              variant="brand-filled"
              size="md"
              data-testid="save-user-form-button"
            >
              Save changes
            </Button>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default UserForm;
